import { fetchWithHandling } from '../utils/common-utils';
import {
  InterventionReportPrefillResponse,
  InterventionReportResponse,
  CreateInterventionReportRequest,
  UpdateInterventionReportRequest,
} from '../utils/interfaces';

const BASE_URL = `${process.env.REACT_APP_BACKEND_API_PREFIX}/api/v1/intervention-reports`;

export const getPrefill = async (
  token: string,
  onSessionExpired: () => void,
  taskId: number
): Promise<InterventionReportPrefillResponse | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/prefill/${taskId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );

    return jsonData as InterventionReportPrefillResponse | null;
  } catch (error) {
    return undefined;
  }
};

export const getById = async (
  token: string,
  onSessionExpired: () => void,
  id: number
): Promise<InterventionReportResponse | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/${id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );

    return jsonData as InterventionReportResponse | null;
  } catch (error) {
    return undefined;
  }
};

export const deleteReport = async (
  token: string,
  onSessionExpired: () => void,
  id: number
): Promise<boolean> => {
  try {
    const res = await fetchWithHandling(
      `${BASE_URL}/${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return res !== null && res !== undefined;
  } catch (error) {
    return false;
  }
};

export const downloadPdf = async (
  token: string,
  onSessionExpired: () => void,
  id: number
): Promise<Blob | undefined> => {
  try {
    const response = await fetchWithHandling(
      `${BASE_URL}/${id}/pdf`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired,
      true
    );

    if (response instanceof Response) {
      return await response.blob();
    }

    return undefined;
  } catch (error) {
    console.error(`Error downloading PDF for intervention report ${id}:`, error);
    return undefined;
  }
};

export const downloadAttachment = async (
  token: string,
  onSessionExpired: () => void,
  reportId: number,
  attachmentId: number
): Promise<Blob | undefined> => {
  try {
    const response = await fetchWithHandling(
      `${BASE_URL}/${reportId}/attachments/${attachmentId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired,
      true
    );

    if (response instanceof Response) {
      return await response.blob();
    }

    return undefined;
  } catch (error) {
    console.error(`Error downloading attachment ${attachmentId} for intervention report ${reportId}:`, error);
    return undefined;
  }
};

export const getNextNumber = async (
  token: string,
  onSessionExpired: () => void
): Promise<number | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/settings/next-number`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );

    return jsonData?.nextNumber as number | undefined;
  } catch (error) {
    return undefined;
  }
};

export const setNextNumber = async (
  token: string,
  onSessionExpired: () => void,
  nextNumber: number
): Promise<boolean> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/settings/next-number`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ nextNumber }),
      },
      onSessionExpired
    );
    return jsonData !== null && jsonData !== undefined;
  } catch (error) {
    return false;
  }
};

// ── Create/Update report submission ─────────────────────────────────────────
// Bypasses fetchWithHandling: the caller needs to distinguish 422 (missing
// signature) / 409 (task not completed, or a report already exists) / 403
// (operator not assigned to the task) instead of a single generic error.

export type SubmitReportResult =
  | { status: 'success'; report: InterventionReportResponse }
  | { status: 'missing_signature' }
  | { status: 'conflict'; existingReportId?: number }
  | { status: 'forbidden' }
  | { status: 'error' };

const submitReport = async (
  url: string,
  method: 'POST' | 'PUT',
  token: string,
  onSessionExpired: () => void,
  data: CreateInterventionReportRequest | UpdateInterventionReportRequest,
  pdfBlob: Blob,
  newAttachmentFiles: File[] = []
): Promise<SubmitReportResult> => {
  try {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    formData.append('pdfFile', pdfBlob, 'rapporto-intervento.pdf');
    newAttachmentFiles.forEach(file => formData.append('attachments', file, file.name));

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.status === 401) {
      onSessionExpired();
      return { status: 'error' };
    }

    if (response.status === 201 || response.status === 200) {
      const report = (await response.json()) as InterventionReportResponse;
      return { status: 'success', report };
    }

    if (response.status === 422) {
      return { status: 'missing_signature' };
    }

    if (response.status === 409) {
      let existingReportId: number | undefined;
      try {
        const body = await response.json();
        existingReportId = body?.existingReportId ?? body?.interventionReportId ?? undefined;
      } catch {
        // no JSON body — ignore, fall back to a generic conflict message
      }
      return { status: 'conflict', existingReportId };
    }

    if (response.status === 403) {
      return { status: 'forbidden' };
    }

    return { status: 'error' };
  } catch (error) {
    console.error('Error submitting intervention report:', error);
    return { status: 'error' };
  }
};

export const createReport = (
  token: string,
  onSessionExpired: () => void,
  data: CreateInterventionReportRequest,
  pdfBlob: Blob,
  newAttachmentFiles: File[] = []
): Promise<SubmitReportResult> =>
  submitReport(BASE_URL, 'POST', token, onSessionExpired, data, pdfBlob, newAttachmentFiles);

export const updateReport = (
  token: string,
  onSessionExpired: () => void,
  id: number,
  data: UpdateInterventionReportRequest,
  pdfBlob: Blob,
  newAttachmentFiles: File[] = []
): Promise<SubmitReportResult> =>
  submitReport(`${BASE_URL}/${id}`, 'PUT', token, onSessionExpired, data, pdfBlob, newAttachmentFiles);
